import { useTranslation } from 'next-i18next/pages'
import { type ListSide, OptionCard } from 'pcl-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { track } from 'track'
import { useIsTouchDevice } from 'use-is-touch-device'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import {
  addReason,
  addReasons,
  clearGenerationError,
  clearLastAddedOptionId,
  deleteOption,
  deleteReason,
  editOption,
  editReason,
  editReasonWeight,
  type ReasonType,
  SideEnum,
  setAiDisabled,
  setGenerationError,
  setIsGenerating,
} from '../state/reducers'
import { makeSelectReasonsByOptionId } from '../state/selectors'
import { isAiDisabled as checkAiDisabled } from '../utils/aiFeatureStatus'
import { generateReasons } from '../utils/generateReasonsApi'
import { isGenerateReasonsRequestWithinSizeLimit } from '../utils/generateReasonsRequestSize'

type OptionProps = {
  option: { label: string; id: string }
  seedReasons?: ReasonType[]
}

function sideToListSide(side: SideEnum): ListSide {
  return side === SideEnum.PRO ? 'PRO' : 'CON'
}

function listSideToSideEnum(side: ListSide): SideEnum {
  return side === 'PRO' ? SideEnum.PRO : SideEnum.CON
}

function toListItem(r: ReasonType) {
  return {
    id: r.id,
    label: r.label,
    weight: r.weight,
    side: sideToListSide(r.side),
    optionId: r.optionId,
  }
}

export function Option({ option: { id, label }, seedReasons }: OptionProps) {
  const dispatch = useAppDispatch()
  const { i18n, t } = useTranslation()
  const selectReasonsByOptionId = useMemo(makeSelectReasonsByOptionId, [])
  const reduxReasons = useAppSelector((s) => selectReasonsByOptionId(s, id))
  const reasons = reduxReasons.length > 0 ? reduxReasons : (seedReasons ?? [])
  const generationError = useAppSelector((s) => s.meta.generationError)
  const hasGenerationError = generationError?.optionId === id
  const [isLocalGenerating, setIsLocalGenerating] = useState(false)
  const aiDisabled = useAppSelector((s) => s.meta.aiDisabled ?? false)
  const hasAiGeneratedReasons = reasons.some((r) => r.aiGenerated)
  const isTouchDevice = useIsTouchDevice()
  const lastAddedOptionId = useAppSelector((s) => s.meta.lastAddedOptionId)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatch(setAiDisabled(checkAiDisabled()))
  }, [dispatch])

  useEffect(() => {
    if (isTouchDevice && id === lastAddedOptionId && containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
      dispatch(clearLastAddedOptionId())
    }
  }, [id, lastAddedOptionId, isTouchDevice, dispatch])

  const showGenerateButton =
    process.env.FEATURES_SHOW_AI_FEATURES === 'true' &&
    process.env.FEATURES_SHOW_AI_GENERATE_FOR_EXISTING_OPTIONS === 'true' &&
    !hasAiGeneratedReasons &&
    !aiDisabled
  const isGenerateDisabled = !isGenerateReasonsRequestWithinSizeLimit({
    optionLabel: label,
    optionId: id,
    reasons: reasons.slice(0, 10),
    language: i18n.language,
  })

  async function handleGenerate() {
    dispatch(setIsGenerating(true))
    setIsLocalGenerating(true)

    const markName = `ai-generate-${id}`
    if (typeof performance !== 'undefined') {
      performance.mark(`${markName}-start`)
    }

    try {
      const response = await generateReasons({
        optionLabel: label,
        optionId: id,
        reasons: reasons.slice(0, 10),
        language: i18n.language,
      })

      if (typeof performance !== 'undefined') {
        performance.mark(`${markName}-end`)
        performance.measure(
          `AI Generation (${label})`,
          `${markName}-start`,
          `${markName}-end`,
        )
      }

      if (response.safetyIdentifier && response.clientIp) {
        console.log('Safety Identifier:', response.safetyIdentifier)
        console.log('Client IP:', response.clientIp)
      }

      const hydrated = response.reasons.map((r) => ({
        ...r,
        optionId: id,
        weight: 0,
      }))
      dispatch(addReasons(hydrated))
      track('event', 'ai-generate-reasons', {
        reasons_count: response.reasons.length,
        context: 'existing_option',
      })
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('Option.generationError')
      dispatch(setGenerationError({ optionId: id, message: msg }))
      dispatch(setAiDisabled(true))
      track('event', 'ai-generate-reasons-error', {
        error_message: msg,
        context: 'existing_option',
      })
    } finally {
      dispatch(setIsGenerating(false))
      setIsLocalGenerating(false)
    }
  }

  const proReasons = reasons
    .filter((r) => r.side === SideEnum.PRO)
    .map(toListItem)
  const conReasons = reasons
    .filter((r) => r.side === SideEnum.CON)
    .map(toListItem)

  return (
    <div ref={containerRef}>
      <OptionCard
        id={id}
        label={label}
        proReasons={proReasons}
        conReasons={conReasons}
        isTouchDevice={isTouchDevice}
        autoFocusPro={!isTouchDevice && id === lastAddedOptionId}
        isGenerating={isLocalGenerating}
        generationError={
          hasGenerationError ? t('Option.generationError') : null
        }
        onDismissError={() => dispatch(clearGenerationError())}
        showGenerateButton={showGenerateButton}
        isGenerateDisabled={isGenerateDisabled}
        onGenerate={handleGenerate}
        onEditTitle={(_, newLabel) =>
          dispatch(editOption({ id, label: newLabel }))
        }
        onDeleteOption={() => dispatch(deleteOption({ id }))}
        onAddReason={(optId, side, lbl) =>
          dispatch(
            addReason({
              side: listSideToSideEnum(side),
              optionId: optId,
              label: lbl,
            }),
          )
        }
        onEditReason={(rId, lbl) =>
          dispatch(editReason({ id: rId, label: lbl }))
        }
        onDeleteReason={(rId) => dispatch(deleteReason({ id: rId }))}
        onToggleReasonWeight={(rId, w) =>
          dispatch(editReasonWeight({ id: rId, weight: w > 0 ? w - 1 : w + 1 }))
        }
        titlePlaceholder={t('Option.titlePlaceholder')}
        deleteLabel={t('Option.deleteOption')}
        generateLabel={t('AddOptionForm.Generate')}
        generatingLabel={t('AddOptionForm.Generating')}
        dismissLabel={t('Option.dismiss')}
        proHeader={t('List.prosHeader')}
        conHeader={t('List.consHeader')}
        addProPlaceholder={t('List.addPro')}
        addConPlaceholder={t('List.addCon')}
        editLabel={t('List.editReason')}
        markKeyPointLabel={t('List.markKeyPoint')}
        unmarkKeyPointLabel={t('List.unmarkKeyPoint')}
      />
    </div>
  )
}
